import React, { useState, useEffect, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../axios';

const MIAH = 'MIAH & SONS INDUSTRIES LTD.';

// Same factory details PiPDF prints for the other companies. Miah & Sons prints no
// letterhead on this letter at all.
const OTHER_LETTERHEADS = {
  'Tanvir Printing & Packaging IND.': {
    address: '746/747 NOLVOG, DHORANGERTEK, TURAG, UTTARA, DHAKA-1230',
    mobile: '01711564795, 01866 990700',
    email: 'tanverpack@yahoo.com',
  },
  'Propack Trims & Accessories Industries': {
    address: 'Muradpur, Shakaswar, Kaliakoir, Gazipur-1703, Bangladesh.',
    mobile: '+1 (555) 123-4567',
    email: 'contact@propackusa.com',
  },
};

const DOCUMENTS = [
  ['Bill of Exchange', 2, 2],
  ['Commercial Invoice', 5, 1],
  ['Packing List', 3, 1],
  ['Delivery Challan', 3, 1],
  ['Certificate of origin', 3, 1],
  ['Beneficiary Certificate', 3, 1],
  ['Truck Receipt', 3, 1],
  ['MUSHAK', 2, 0],
];

const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN',
  'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

function underThousand(n) {
  const parts = [];
  if (n >= 100) { parts.push(`${ONES[Math.floor(n / 100)]} HUNDRED`); n %= 100; }
  if (n >= 20) { parts.push(TENS[Math.floor(n / 10)]); n %= 10; }
  if (n > 0) parts.push(ONES[n]);
  return parts.join(' ');
}

function integerToWords(n) {
  if (n === 0) return 'ZERO';
  const parts = [];
  [[1e9, 'BILLION'], [1e6, 'MILLION'], [1e3, 'THOUSAND']].forEach(([size, label]) => {
    if (n >= size) { parts.push(`${underThousand(Math.floor(n / size))} ${label}`); n %= size; }
  });
  if (n > 0) parts.push(underThousand(n));
  return parts.join(' ');
}

// Matches the letters Sabbir sends: "THREE HUNDRED EIGHTY SEVEN DOLLARS & THREE CENTS ONLY"
function amountInWords(amount) {
  const totalCents = Math.round((Number(amount) || 0) * 100);
  const dollars = Math.floor(totalCents / 100);
  const cents = totalCents % 100;
  return `${integerToWords(dollars)} DOLLARS${cents ? ` & ${integerToWords(cents)} CENTS` : ''} ONLY`;
}

const usd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dhakaDate = (date) => date.toLocaleDateString('en-GB', {
  timeZone: 'Asia/Dhaka', day: '2-digit', month: '2-digit', year: 'numeric',
}).replace(/\//g, '.');

const clean = (s) => (s || '').replace(/\s+/g, ' ').trim();

function Letterhead({ company }) {
  const other = OTHER_LETTERHEADS[company];
  if (!other) return null;
  return (
    <>
      <div style={{ fontSize: '25px', fontWeight: 'bold', color: '#161515ac', lineHeight: '32px' }}>{company}</div>
      <div style={{ fontSize: '13px', lineHeight: '19px', marginTop: '4px' }}>
        <strong>Factory Address:</strong> {other.address}<br />
        Mobile No: {other.mobile}<br />
        <span style={{ color: '#d2691e' }}><strong>E-mail:</strong> {other.email}</span>
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #000', margin: '8px 0 15px' }} />
    </>
  );
}

// Memoised so edits typed into the live DOM are never overwritten by a re-render
// of the toolbar; it only re-renders when the letter data itself changes.
const FreshLetter = memo(function FreshLetter({ data, refNo, company }) {
  const { commercial, partyBank, ourBank, lcValue } = data;
  const lcDate = commercial.LcDate ? dhakaDate(new Date(commercial.LcDate * 1000)) : '';
  // Same amendment wording as the BTB L/C row on the commercial documents.
  const amendmentText = [
    clean(commercial.AmendmentInfo) && `NUMBER OF AMENDMENT: ${clean(commercial.AmendmentInfo)}`,
    Number(commercial.AmendmentDate) > 0 && `DATED: ${dhakaDate(new Date(commercial.AmendmentDate * 1000))}`,
  ].filter(Boolean).join(', ');
  const partyBankText = [clean(partyBank?.Name), clean(partyBank?.Address)].filter(Boolean).join(', ');

  const cell = { border: '1px solid #000', padding: '4px 8px' };

  return (
    <div className="fl-letter" style={{ fontSize: '14px', lineHeight: 1.5, color: '#000' }}>
      <Letterhead company={company} />

      <div style={{ marginTop: '10px' }}>
        <strong>Ref: <span data-fl-ref="1">{refNo}</span></strong>
      </div>
      <div><strong>Date: <span>{dhakaDate(new Date())}</span></strong></div>

      <div style={{ marginTop: '14px' }}>To</div>
      <div>The Manager</div>
      <div>{ourBank?.Name || '[Bank name]'}</div>
      <div>{clean(ourBank?.Address) || '[Branch address]'}</div>
      <div><strong>Routing No. : <span>{ourBank?.RoutingNo || ''}</span></strong></div>

      <div style={{ marginTop: '14px', fontWeight: 'bold', textDecoration: 'underline' }}>
        Subject: Application For The Following Negotiation Documents For USD {usd(lcValue)} against
        L/C NO: {commercial.LcNo}, DATED: {lcDate}, {amendmentText && `${amendmentText}, `}{partyBankText}
      </div>

      <div style={{ marginTop: '14px' }}>Dear Sir,</div>
      <div style={{ textAlign: 'justify' }}>
        We Do Here by Submitting the Following Documents for Negotiation of USD = {usd(lcValue)} Say
        ( United States Dollar {amountInWords(lcValue)}) Against delivery of garments accessories as per
        Pro-forma Invoice.
      </div>
      <div>A/C: {commercial.PartyName}</div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '14px' }}>
        <thead>
          <tr>
            <th style={{ ...cell, width: '8%', textAlign: 'center' }}>SL</th>
            <th style={{ ...cell, textAlign: 'left' }}>Particular's</th>
            <th style={{ ...cell, width: '18%', textAlign: 'center' }}>Photo Copy</th>
            <th style={{ ...cell, width: '18%', textAlign: 'center' }}>Original Copy</th>
          </tr>
        </thead>
        <tbody>
          {DOCUMENTS.map(([name, photo, original], i) => (
            <tr key={name}>
              <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
              <td style={cell}>{name}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{photo}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{original}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '14px' }}>
        In view of the above, we would request you to negotiate the above stated matter at earliest.
      </div>
      <div style={{ marginTop: '14px' }}>Thanking You.</div>
      <div>Sincerely yours,</div>

      <table style={{ marginTop: '18px' }} width="100%" border="0" cellSpacing="0" cellPadding="0">
        <tbody>
          <tr>
            <td width="50%" align="center">&nbsp;</td>
            <td width="50%" align="center">
              <strong style={{ fontSize: '15px' }}>{company}</strong>
              <br />
              <img style={{ maxWidth: '150px' }} src="/Images/miah_and_sons_signature.png" alt="AUTHORISED SIGNATURE" />
              <br />
              <span style={{ borderTop: '1px solid black', fontSize: '12px' }}>AUTHORISED SIGNATURE</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

// Blank space left at the top of the page for the pre-printed letterhead pad.
const PAD_GAP = '2.25in';

const padStorageKey = (company) => `forwardingLetterPad:${company}`;
const readPadSetting = (company) => {
  try {
    const stored = localStorage.getItem(padStorageKey(company));
    if (stored !== null) return stored === '1';
  } catch (_) { /* storage blocked: fall back to the default */ }
  // Miah & Sons prints this letter on its letterhead pad; the others print their own heading.
  return company === MIAH;
};

const ForwardingLetterPDF = () => {
  const { commercialId } = useParams();
  const company = localStorage.getItem('company') || MIAH;

  const [data, setData] = useState(undefined);
  const [mode, setMode] = useState('fresh'); // 'fresh' = built from data, 'saved' = stored HTML
  const [savedHtml, setSavedHtml] = useState('');
  const [refNo, setRefNo] = useState('');
  const [serialInput, setSerialInput] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [freshKey, setFreshKey] = useState(0);
  const [pad, setPad] = useState(() => readPadSetting(company));
  const containerRef = useRef(null);

  const previewRef = (d, serial) => {
    const year = new Date().toLocaleDateString('en-GB', { timeZone: 'Asia/Dhaka', year: 'numeric' });
    return `${d.refPrefix}/${d.bankCode}/${String(serial).padStart(4, '0')}/${year}`;
  };

  useEffect(() => {
    axios.get(`${window.backendUrl}/get_forwarding_letter/${commercialId}`, { params: { company } })
      .then((res) => {
        const d = res.data;
        setData(d);
        if (d.saved) {
          setRefNo(d.saved.refNo);
          setSavedAt(d.saved.savedAt);
          if (d.saved.htmlContent) {
            setSavedHtml(d.saved.htmlContent);
            setMode('saved');
          }
        } else {
          setSerialInput(String(d.nextSerial));
          setRefNo(previewRef(d, d.nextSerial));
        }
      })
      .catch((err) => {
        console.error(err);
        setData(null);
      });
  }, [commercialId, company]);

  // The whole letter is one editable area, so lines mixing plain and bold text
  // (Ref, Date, Routing No., letterhead address) can be edited and new lines added.
  // Old per-element flags (from letters saved before) are cleared, since a nested
  // contenteditable="false" would lock that part again.
  useEffect(() => {
    if (!containerRef.current) return;
    const root = containerRef.current.querySelector('.fl-letter') || containerRef.current.firstChild;
    if (!root) return;
    root.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
    root.setAttribute('contenteditable', editMode ? 'true' : 'false');
  }, [editMode, mode, savedHtml, freshKey, data]);

  const handleSerialChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setSerialInput(digits);
    if (!digits) return;
    const ref = previewRef(data, Number(digits));
    setRefNo(ref);
    // Write the new ref straight into the letter so edits typed elsewhere are kept.
    const refEl = containerRef.current?.querySelector('[data-fl-ref]');
    if (refEl) refEl.textContent = ref;
  };

  const togglePad = (checked) => {
    setPad(checked);
    try { localStorage.setItem(padStorageKey(company), checked ? '1' : '0'); } catch (_) { /* not remembered */ }
  };

  const handleSave = async () => {
    if (!containerRef.current) return;
    const firstSave = !data.saved && mode === 'fresh';
    if (firstSave && !Number(serialInput)) {
      alert('Enter the serial number for this letter.');
      return;
    }
    setSaving(true);
    setJustSaved(false);
    try {
      const res = await axios.post(`${window.backendUrl}/reserve_forwarding_letter_no/${commercialId}`,
        { company, ...(firstSave ? { serial: Number(serialInput) } : {}) });
      const reservedRef = res.data.refNo;
      // The page showed a preview number; stamp the number actually taken.
      const refEl = containerRef.current.querySelector('[data-fl-ref]');
      if (refEl && mode === 'fresh') refEl.textContent = reservedRef;

      // Capture the letter root itself so saved and fresh letters store the same
      // shape (capturing the container would nest one wrapper per save).
      const root = containerRef.current.querySelector('.fl-letter') || containerRef.current;
      const snapshot = root.cloneNode(true);
      snapshot.removeAttribute('contenteditable');
      snapshot.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
      const html = root === containerRef.current ? snapshot.innerHTML : snapshot.outerHTML;

      const saveRes = await axios.post(`${window.backendUrl}/save_forwarding_letter/${commercialId}`, { htmlContent: html });
      setRefNo(saveRes.data.refNo || reservedRef);
      setSavedHtml(html);
      setSavedAt(new Date().toISOString());
      setData((d) => ({ ...d, saved: { refNo: saveRes.data.refNo || reservedRef, htmlContent: html } }));
      setEditMode(false);
      setMode('saved');
      setJustSaved(true);
      if (saveRes.data.warning) alert(saveRes.data.warning);
    } catch (err) {
      console.error('Failed to save forwarding letter:', err);
      alert(err.response?.data?.error || 'Could not save the forwarding letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setEditMode(false);
    // Reset only the inner copy; the container itself belongs to React.
    if (mode === 'saved' && containerRef.current?.firstChild) containerRef.current.firstChild.innerHTML = savedHtml;
    if (mode === 'fresh') setFreshKey((k) => k + 1);
  };

  const handleRegenerate = () => {
    if (!window.confirm('Rebuild this letter from the latest LC data? Your saved edits stay until you save again.')) return;
    setEditMode(false);
    setFreshKey((k) => k + 1);
    setMode('fresh');
  };

  if (data === undefined) return null;
  if (data === null) return <p style={{ margin: 20 }}>Could not load the forwarding letter for this commercial.</p>;

  const reserved = !!data.saved || mode === 'saved';
  const last = data.lastLetter;

  return (
    <div style={{ display: 'block', fontSize: '14px', margin: '0px 20px' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: ${pad ? '0 10mm 8mm' : '10mm'}; }
          .no-print { display: none !important; }
          [contenteditable] { outline: none !important; background-color: transparent !important; }
          .fl-pad-gap { border: none !important; }
          .fl-pad-gap span { display: none !important; }
          /* Slightly tighter lines so the letter still fits one page below the pad heading. */
          .fl-pad .fl-letter { line-height: 1.4 !important; }
          .fl-pad .fl-letter td, .fl-pad .fl-letter th { padding-top: 3px !important; padding-bottom: 3px !important; }
        }
        .fl-pad-gap {
          height: ${PAD_GAP};
          border: 1px dashed #bbb;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .fl-edit-mode [contenteditable="true"] {
          outline: 1px dashed #3b82f6;
          background-color: rgba(59, 130, 246, 0.05);
          cursor: text;
          min-width: 12px;
        }
      `}</style>

      <div className="no-print" style={{ margin: '8px 0 12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode((prev) => !prev)}>{editMode ? 'Normal View' : 'Edit Content'}</button>
        {editMode && <button onClick={handleDiscard}>Discard Changes</button>}
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Letter'}</button>
        <button onClick={() => window.print()}>Print</button>
        {mode === 'saved' && <button onClick={handleRegenerate}>Regenerate from latest data</button>}
        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
          <input type="checkbox" checked={pad} onChange={(e) => togglePad(e.target.checked)} />
          Print on letterhead pad (leave top space)
        </label>
      </div>

      <div className="no-print" style={{ margin: '0 0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {!reserved && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
            Serial No.
            <input
              value={serialInput}
              onChange={(e) => handleSerialChange(e.target.value)}
              inputMode="numeric"
              style={{ width: '80px', padding: '2px 6px' }}
            />
          </label>
        )}
        <span style={{ color: justSaved ? '#198754' : '#555', fontWeight: justSaved ? 'bold' : 'normal' }}>
          {justSaved && '✓ Saved. '}
          {reserved
            ? `Ref ${refNo}${savedAt && mode === 'saved' ? ` · saved ${new Date(savedAt).toLocaleString('en-GB', { timeZone: 'Asia/Dhaka' })}` : ''}${mode === 'fresh' ? ' · regenerated, not saved yet' : ''}`
            : `Not saved yet — ${refNo} is a preview; the number is taken when you save.`}
        </span>
        {last && (
          <span style={{ color: '#555' }}>
            · Last {data.bankCode} letter: <strong>{last.refNo}</strong> (Com #{last.commercialId})
          </span>
        )}
      </div>

      {pad && <div className="fl-pad-gap" style={{ maxWidth: '800px' }}><span>Letterhead pad area — left blank when printing</span></div>}

      <div ref={containerRef} className={[editMode ? 'fl-edit-mode' : '', pad ? 'fl-pad' : ''].join(' ').trim()} style={{ maxWidth: '800px' }}>
        {mode === 'saved'
          ? <div dangerouslySetInnerHTML={{ __html: savedHtml }} />
          : <FreshLetter key={freshKey} data={data} refNo={refNo} company={company} />}
      </div>
    </div>
  );
};

export default ForwardingLetterPDF;
